import React, { useMemo } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { isAuthorized, tryToLogin, tryToLoginWithPhoneNumberFlow } from './utils/auth';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from './redux/selectors/user';
import {
  checkIsRestrictedForDisabledUsers,
  checkIsRestrictedForNonPortalUser,
  getRouteGroup,
  RouteGroup,
  routes
} from './utils/routerHelper';
import EmptyPage, { EMPTY_PAGE_USECASE } from './screens/pageNotFound/EmptyPage';
import { getAnalytikaScreenName } from './utils/analytika/reporterAnalytika';
import { accountDisabledRedirect, noPortalAccessRedirect, otpDisabledRedirect } from './utils/analytika/events';
import Page from './components/Page/Page';
import publishAV2Event from './utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from './utils/analytika/enums';
import { SkeletonPageSet } from './components/WithSkeleton/WithSkeleton';
import { FeatureFlag } from './utils/galileo/featureFlag.enum';

type ProtectedComponentProps = {
  component: any;
  componentProps: any;
  path?: string | string[];
  [propKey: string]: any;
};

const ProtectedComponent = ({ component: Component, path, ...componentProps }: ProtectedComponentProps) => {
  const {
    merchantConfig: {
      isOtpEnabled,
      isMerchantEnabled,
      accessTypes: { isPortalAccessEnabled }
    },
    userGalileoVars: {
      portal_rollout_toggling_otp: isRolloutTogglingOtpEnabled,
      portal_is_access_type_controlled: isAccessTypeControlled,
      [FeatureFlag.IS_NEW_SIGN_IN_ENABLED]: isNewSignInEnabled
    },
    loadCompleteFor: { blockingRequests: isBlockingRequestsCompleted, galileoVars: isGalileoVarsLoaded }
  } = useSelector(userSelector);

  const {
    user: { publishAnalytikaEvent }
  } = useDispatch<{
    user: { publishAnalytikaEvent: (value: any) => void };
  }>();

  const routeGroup = path && getRouteGroup(path);

  const skeletonSet = useMemo(
    () => (
      <Page>
        <SkeletonPageSet />
      </Page>
    ),
    []
  );

  const isUserAuthorized = isAuthorized();

  //TODO: only for new sign in rollout. Remove after 100% rollout
  if (!isGalileoVarsLoaded && !isUserAuthorized) {
    return skeletonSet;
  } else if (isNewSignInEnabled && !isUserAuthorized) {
    tryToLoginWithPhoneNumberFlow();
    return skeletonSet;
  } else if (!isNewSignInEnabled && !isUserAuthorized) {
    tryToLogin();
    return skeletonSet;
  }

  if (!isBlockingRequestsCompleted) {
    return skeletonSet;
  }

  // if OTP section is not enabled for user
  const isOtpVisible = (isRolloutTogglingOtpEnabled && isOtpEnabled) || !isRolloutTogglingOtpEnabled;
  const isOtpGroup = routeGroup === RouteGroup.DEDICATED_FLEET;

  // if user's account is disabled
  const isPageRestrictedForDisabled = path && !isMerchantEnabled && checkIsRestrictedForDisabledUsers(path);

  // if user doesn't have access for creating orders from Portal
  const isOrderCreationDisabled =
    path && isAccessTypeControlled && !isPortalAccessEnabled && checkIsRestrictedForNonPortalUser(path);

  if (isPageRestrictedForDisabled) {
    const currentScreenName = getAnalytikaScreenName(path);
    publishAnalytikaEvent(accountDisabledRedirect(currentScreenName));
    publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_REDIRECT_ACC_DISABLED, event_version: 6 });
    return <EmptyPage useCase={EMPTY_PAGE_USECASE.ACCOUNT_DISABLED} />;
  }

  if (isOrderCreationDisabled) {
    const currentScreenName = getAnalytikaScreenName(path);
    publishAnalytikaEvent(noPortalAccessRedirect(currentScreenName));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_REDIRECT_NO_PORTALACCESS,
      event_version: 6
    });
    return <Redirect to={routes.deliveriesDashboard} />;
  }

  if (isOtpGroup && !isOtpVisible) {
    const currentScreenName = getAnalytikaScreenName(path);
    publishAnalytikaEvent(otpDisabledRedirect(currentScreenName));
    return <EmptyPage useCase={EMPTY_PAGE_USECASE.PAGE_NOT_FOUND} />;
  }

  return <Component {...componentProps} />;
};

const ProtectedRoute = ({ component, componentProps, path, ...rest }: ProtectedComponentProps) => (
  <Route
    {...rest}
    path={path}
    render={props => <ProtectedComponent path={path} {...props} {...componentProps} component={component} />}
  />
);

export default ProtectedRoute;
